/**
 * What an operator reads when a request fails. The servers answer with a stable code and an
 * English sentence meant for support; the Control Center speaks the operator's language, so:
 *
 * - a message already written for people (Vietnamese text, e.g. the core validator's refusals)
 *   is shown as it is;
 * - otherwise the code's own sentence (`common:apiErrors.<CODE>`), else one for the kind of
 *   failure (`common:apiErrors.status.<class>`), and the server's sentence is kept as a
 *   technical detail for support (folded away).
 */

/** Vietnamese letters with diacritics: the message was written for people, in their language. */
const VIETNAMESE = /[àáảãạăằắẳẵặâầấẩẫậèéẻẽẹêềếểễệìíỉĩịòóỏõọôồốổỗộơờớởỡợùúủũụưừứửữựỳýỷỹỵđ]/i;

export interface ErrorText {
  /** The sentence shown to the operator. */
  text: string;
  /** The server's own sentence when it is not shown as `text` (for support). */
  detail: string | null;
}

interface ErrorLike {
  code?: unknown;
  message?: unknown;
  status?: unknown;
}

function statusClass(status: number, code: string): string {
  if (code === 'NETWORK_ERROR' || status === 0) return 'network';
  if (status === 401) return 'unauthenticated';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'notFound';
  if (status === 409) return 'conflict';
  if (status === 429) return 'rateLimited';
  if (status >= 500) return 'server';
  if (status >= 400) return 'invalid';
  return 'other';
}

/**
 * [`errorText`] with its strings given: `lookup(key)` answers a `common:apiErrors.*` key or
 * `undefined`; `vietnamese` says whether the operator reads Vietnamese.
 */
export function errorTextWith(error: unknown, lookup: (key: string) => string | undefined, vietnamese: boolean): ErrorText {
  const e = (typeof error === 'object' && error !== null ? error : {}) as ErrorLike;
  const message = typeof e.message === 'string' ? e.message.trim() : typeof error === 'string' ? error.trim() : '';
  const code = typeof e.code === 'string' ? e.code : '';
  const status = typeof e.status === 'number' ? e.status : -1;
  // Written for people in the operator's language already (in English, any message without a code).
  if (message && (vietnamese ? VIETNAMESE.test(message) : !code)) return { text: message, detail: null };
  const text =
    (code ? lookup(`apiErrors.codes.${code}`) : undefined) ??
    lookup(`apiErrors.status.${status >= 0 || code ? statusClass(status, code) : 'other'}`) ??
    message;
  return { text, detail: message && message !== text ? message : null };
}
